import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import type { DashboardAlert } from '@/shared/types/domain.types';

interface AlertCardProps {
  alert: DashboardAlert;
  onClick: () => void;
  showLocationName?: boolean;
}

/**
 * Alert icon mapping
 */
const ALERT_ICONS: Record<string, string> = {
  OVERDUE_ORDERED: '🔴',
  STALE_PENDING: '🟡',
  RECEIVING_BACKLOG: '📦',
  ORDERING_DAY: '📅',
  LOW_USAGE: '📊',
};

/**
 * Badge variant mapping
 */
function getBadgeVariant(severity: string): 'success' | 'warning' | 'danger' | 'info' {
  switch (severity) {
    case 'red':
      return 'danger';
    case 'yellow':
      return 'warning';
    case 'green':
      return 'success';
    case 'info':
      return 'info';
    default:
      return 'info';
  }
}

/**
 * Get severity label
 */
function getSeverityLabel(severity: string): string {
  switch (severity) {
    case 'red':
      return 'Critical';
    case 'yellow':
      return 'Warning';
    case 'info':
      return 'Info';
    default:
      return '';
  }
}

export function AlertCard({ alert, onClick, showLocationName = false }: AlertCardProps) {
  const icon = ALERT_ICONS[alert.type] || '⚠️';
  const badgeVariant = getBadgeVariant(alert.severity);
  const severityLabel = getSeverityLabel(alert.severity);

  return (
    <Card
      padding="md"
      className="cursor-pointer hover:shadow-lg transition-shadow border-l-4"
      style={{
        borderLeftColor:
          alert.severity === 'red'
            ? '#dc2626'
            : alert.severity === 'yellow'
            ? '#eab308'
            : alert.severity === 'info'
            ? '#3b82f6'
            : '#6b7280',
      }}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <span className="text-2xl" role="img" aria-label="alert-icon">
            {icon}
          </span>
          <div>
            {showLocationName && (
              <p className="text-sm font-medium text-gray-500 mb-1">
                <span className="hidden lg:inline">{alert.location_name}</span>
                <span className="lg:hidden">{alert.location_short_code || alert.location_name}</span>
              </p>
            )}
            <p className="text-lg font-semibold text-gray-900">{alert.message}</p>
            {alert.count > 0 && alert.type !== 'LOW_USAGE' && (
              <p className="text-sm text-gray-600 mt-1">Click to view and take action</p>
            )}
            {alert.type === 'LOW_USAGE' && (
              <p className="text-sm text-gray-600 mt-1">Click to view activity reports</p>
            )}
          </div>
        </div>
        {severityLabel && (
          <Badge variant={badgeVariant} size="sm">
            {severityLabel}
          </Badge>
        )}
      </div>
    </Card>
  );
}
